import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Radar, X } from 'lucide-react';
import { REGIONS, setRegionOverride, useVisitorRegion } from '../utils/currencyLocale';
import { IOS_APP_PAGE_LIVE, IOS_APP_PATH } from '../data/iosApp';
import AppStoreGlyph from './AppStoreGlyph';
import './PublicHeader.css';

// The Guides hub already surfaces the CBAT Guide as its primary CTA
// (GuidesPage.js's hero button), so a single "Guides" nav entry reaches
// both without dropping either page from the site.
const militaryLinks = [
  ['/raf-cbat-practice', 'Practice Tests'],
  ['/preparation-plan', 'Plan Builder'],
  ['/guides', 'Guides'],
  ['/pricing', 'Pricing'],
  ['/commercial', 'Commercial Pilot Prep']
];

// Shown instead of militaryLinks on the commercial public pages -- the
// mirror image of militaryLinks' cross-link, so each side always offers a
// way to the other rather than assuming the visitor found the right one.
const commercialLinks = [
  ['/commercial', 'Overview'],
  ['/commercial-guide', 'Guide'],
  ['/commercial-preparation-plan', 'Plan Builder'],
  ['/commercial-pricing', 'Pricing'],
  ['/raf-cbat', 'Military CBAT']
];

const PublicHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [regionMenuOpen, setRegionMenuOpen] = useState(false);
  // The live region, not one read at mount: it follows the visitor's own
  // pick and the country lookup landing, the same as every page that shows
  // a price does.
  const { region } = useVisitorRegion();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isCommercial = pathname.startsWith('/commercial');
  const publicLinks = isCommercial ? commercialLinks : militaryLinks;
  const practicePath = isCommercial ? '/auth?view=signup' : pathname.startsWith('/cfast') ? '/cfast-practice' : '/raf-cbat-practice';
  const practiceLabel = isCommercial ? 'Create account' : 'Try free practice';
  const currentRegion = REGIONS.find((candidate) => candidate.code === region) || REGIONS[0];

  // A visitor changing their region on the homepage itself gets the same
  // track-specific landing page auto-detection would have sent them to --
  // anywhere else on the site, the choice only affects currency (every page
  // that shows a price follows it via useVisitorRegion()) and doesn't force
  // a navigation away from what they're already reading.
  const selectRegion = (code) => {
    setRegionOverride(code);
    setRegionMenuOpen(false);
    const targetRoute = REGIONS.find((candidate) => candidate.code === code)?.homepageRoute;
    if (pathname === '/' && targetRoute && targetRoute !== '/') navigate(targetRoute);
  };

  return (
    <header className="public-header">
      <div className="public-header__inner">
        <Link className="public-header__brand" to="/" aria-label="CBAT Academy home" onClick={() => setMenuOpen(false)}>
          <span className="public-header__mark"><Radar aria-hidden="true" /></span>
          <span>
            <strong>CBAT<span>//</span>ACADEMY</strong>
            <small>Cognitive training system</small>
          </span>
        </Link>
        <div className="public-header__region" data-testid="region-selector">
          <button
            type="button"
            className="public-header__region-toggle"
            aria-label={`Region and currency: ${currentRegion.label}. Change region`}
            aria-expanded={regionMenuOpen}
            aria-haspopup="menu"
            onClick={() => setRegionMenuOpen((open) => !open)}
            data-testid="region-selector-toggle"
          >
            <span aria-hidden="true" data-testid="region-selector-flag">{currentRegion.flag}</span>
          </button>
          <div className="public-header__region-menu" role="menu" aria-label="Choose your region" data-testid="region-selector-menu" hidden={!regionMenuOpen}>
            {REGIONS.map((candidate) => (
              <button
                key={candidate.code}
                type="button"
                role="menuitem"
                className="public-header__region-option"
                aria-current={candidate.code === region ? 'true' : undefined}
                onClick={() => selectRegion(candidate.code)}
                data-region-code={candidate.code}
                data-testid={`region-option-${candidate.code}`}
              >
                <span aria-hidden="true">{candidate.flag}</span> <span>{candidate.label}</span>
              </button>
            ))}
          </div>
        </div>
        <nav className="public-header__nav" aria-label="Public navigation">
          {publicLinks.map(([to, label]) => <Link to={to} key={to}>{label}</Link>)}
        </nav>
        {/* Outside the collapsing nav, like the region selector, so phone
            visitors -- the people who can actually install it -- always see
            it. Military side only: the app has no commercial modules yet. */}
        {IOS_APP_PAGE_LIVE && !isCommercial && (
          <Link
            className="public-header__app"
            to={IOS_APP_PATH}
            aria-label="Get the app for iPhone and iPad"
            onClick={() => setMenuOpen(false)}
            data-testid="public-header-get-app"
          >
            <AppStoreGlyph className="public-header__app-glyph" />
            <span>Get the app</span>
          </Link>
        )}
        <div className="public-header__actions">
          <Link className="public-header__login" to="/auth?view=signin">Sign In</Link>
          <Link className="public-header__register" to={practicePath}>{practiceLabel}</Link>
        </div>
        <button
          className="public-header__menu-button"
          type="button"
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
          aria-controls="public-mobile-navigation"
          onClick={() => setMenuOpen((open) => !open)}
          data-testid="public-navigation-toggle"
        >
          {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
      </div>
      <nav
        id="public-mobile-navigation"
        className="public-header__mobile"
        aria-label="Mobile public navigation"
        data-testid="public-navigation-menu"
        hidden={!menuOpen}
      >
        {publicLinks.map(([to, label]) => <Link to={to} key={to} onClick={() => setMenuOpen(false)}>{label}</Link>)}
        {/* The bar only has room for the button's icon on a phone, so the
            menu repeats it with its label. */}
        {IOS_APP_PAGE_LIVE && !isCommercial && <Link to={IOS_APP_PATH} onClick={() => setMenuOpen(false)}>Get the app for iPhone and iPad</Link>}
        <Link to="/auth?view=signin" onClick={() => setMenuOpen(false)}>Sign In</Link>
        <Link className="public-header__mobile-cta" to={practicePath} onClick={() => setMenuOpen(false)}>{practiceLabel}</Link>
      </nav>
    </header>
  );
};

export default PublicHeader;
